'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { cn } from '@cobrastyle/ui';
import type { FreeGift } from '@/lib/free-gift/types';
import { formatPrice } from '@/lib/format-price';
import { ProductBadge } from './product-badge';

interface FreeGiftOfferProps {
  /** The gift the product grants. Null renders nothing. */
  gift: FreeGift | null;
  /** Whether the customer has opted in. */
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Currency of the gift's price. Defaults to the store's SEK. */
  currency?: string;
  /** Locks the checkbox while an add-to-cart is in flight. */
  disabled?: boolean;
}

/**
 * The "Ingår" block on the product page: the gift, its price struck through
 * against zero, and an opt-in checkbox. Unchecked by default — the customer
 * claims the gift deliberately.
 *
 * The whole panel is one label, so a click anywhere claims the gift, and the
 * panel's frame carries the claimed state rather than relying on a small
 * checkbox alone. An out-of-stock gift stays visible but inert, so the offer
 * doesn't silently disappear between visits.
 *
 * The canvas has no free-gift panel, so this borrows the buy box's own
 * vocabulary instead: it is drawn as a sibling of the licence notice — same 4px
 * radius, same accent rule and 6% tint when active — and its "Ingår" chip is
 * literally the listing card's accent badge, so the two agree. Prices go
 * through the shared formatter for the same reason the rest of this page does:
 * the design system's <Price> brings its own scale and colour.
 */
export function FreeGiftOffer({
  gift,
  checked,
  onChange,
  currency = 'SEK',
  disabled = false,
}: FreeGiftOfferProps) {
  const t = useTranslations('product');

  if (!gift) return null;

  const unavailable = !gift.inStock;
  const locked = unavailable || disabled;
  const claimed = checked && !unavailable;

  return (
    <section
      className={cn(
        'group overflow-hidden rounded border transition-colors',
        claimed ? 'border-accent-600/40 bg-accent-600/[0.06]' : 'border-border bg-card',
        unavailable && 'opacity-60',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-border px-4 py-2.5">
        <ProductBadge tone="accent" className="inline-flex items-center gap-1.5">
          <GiftIcon className="h-2.5 w-2.5" />
          {t('freeGift.included')}
        </ProductBadge>
        <span className="text-[11px] uppercase tracking-[0.08em] text-muted">
          {unavailable ? t('freeGift.outOfStock') : t('freeGift.title')}
        </span>
      </div>

      <label
        className={cn(
          'flex items-center gap-3.5 px-4 py-3.5',
          locked ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={locked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />

        {/* Custom control: the native checkbox renders in the browser's own
            blue, which fights the store's olive accent. */}
        <span
          aria-hidden="true"
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-[2px] border-2 transition-colors',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
            claimed
              ? 'border-accent-700 bg-accent-700 text-white'
              : 'border-foreground/30 bg-input',
            !locked && !claimed && 'group-hover:border-foreground/60',
          )}
        >
          {claimed && (
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>

        {/* `rounded-[3px]` and the recessed ground match the gallery thumbnails. */}
        {gift.image ? (
          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-[3px] border border-border bg-surface-100">
            <Image src={gift.image} alt={gift.name} fill className="object-contain p-1" sizes="44px" />
          </div>
        ) : (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[3px] border border-border bg-accent-600/[0.08]">
            <GiftIcon className="h-5 w-5 text-accent-700" />
          </div>
        )}

        <span className="min-w-0 flex-1">
          <span className="block text-[14.5px] font-semibold leading-snug text-foreground">
            {gift.name}
          </span>

          <span className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="font-heading text-[15px] font-bold leading-none text-accent-700">
              {formatPrice(0, currency)}
            </span>
            <span className="text-[12.5px] text-foreground/40 line-through">
              {formatPrice(gift.price, currency)}
            </span>
          </span>
        </span>
      </label>
    </section>
  );
}

function GiftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2zM12 7v14" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"
      />
    </svg>
  );
}
